import { getFolderFiles } from '@/user_files/files/fetch/get-folder-files'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { isSelectableProcessingStatus } from '@/user_files/utils/processing-status'
import { uniquePreserveOrder } from '@/util/array-utils'

export type ResolvedChatFiles = {
  /** Ids the Assistant can actually be given: `COMPLETED` files, deduplicated, ticked order first. */
  attachableFileIds: string[]
  /** How many covered files were dropped because the Assistant cannot read them yet. */
  skippedCount: number
  /** Ticked folders that turned out to hold no files at all, so the outcome can explain itself. */
  emptyFolderCount: number
}

type ResolveParams = {
  /** Records for the file rows the user ticked. */
  selectedFiles: UserFileRead[]
  /** Folder ids the user ticked; each contributes its whole subtree. */
  selectedFolderIds: string[]
  signal?: AbortSignal
  /** Called with the records pulled out of folders, so their names resolve in the chat's chips. */
  onFolderFilesLoaded?: (files: UserFileRead[]) => void
}

/**
 * Resolve a Matter Files selection into the file ids that can be attached to an Assistant chat.
 *
 * A selected folder contributes the files inside it, which is what the user means by ticking it and
 * what the design shows. Folders are expanded one at a time rather than in parallel: a matter-wide
 * search select-all can tick hundreds of folders, and `getFolderFiles` drains every page of each,
 * so a fan-out here is a request storm rather than a speed-up.
 *
 * `statuses` is deliberately not passed. Filtering server-side would make a folder's unattachable
 * files disappear with nothing left to count, and the product decision is to skip them *and say so*.
 * Everything active comes back and the split happens here, so a file inside a folder is treated
 * exactly like one the user ticked directly.
 *
 * @throws whatever `getFolderFiles` throws, including an abort — the caller must not create a chat
 *   from a half-resolved selection.
 */
export async function resolveSelectedFilesForChat({
  selectedFiles,
  selectedFolderIds,
  signal,
  onFolderFilesLoaded,
}: ResolveParams): Promise<ResolvedChatFiles> {
  const coveredFiles: UserFileRead[] = [...selectedFiles]
  let emptyFolderCount = 0

  for (const folderId of selectedFolderIds) {
    const response = await getFolderFiles(folderId, { recursive: true, signal })
    if (response.files.length === 0) emptyFolderCount += 1
    onFolderFilesLoaded?.(response.files)
    coveredFiles.push(...response.files)
  }

  // Dedupe before splitting, not after: in search results a file can be ticked *and* live inside a
  // ticked folder, and counting it in either bucket twice would misreport both numbers.
  const uniqueIds = uniquePreserveOrder(coveredFiles.map((file) => file.id))
  const statusById = new Map(coveredFiles.map((file) => [file.id, file.processing_status]))

  const attachableFileIds = uniqueIds.filter((id) => isSelectableProcessingStatus(statusById.get(id)))

  return { attachableFileIds, skippedCount: uniqueIds.length - attachableFileIds.length, emptyFolderCount }
}
